// --- React & Core Dependencies
import React, { useState } from 'react';
// --- UI Components
import Tabs from '@dtci/spellbook/Tabs';
import Tab from '@dtci/spellbook/Tab';
import Card from '@dtci/spellbook/Card';
import { ClearanceMemoCard } from '../cards/clearance-memo';
import { ClearanceReportCard } from '../cards/clearance-report';
import {usePermissions} from "../../../utils/permissions";

/**
 * ClearanceMemoTabs Component
 *
 * This component renders tabs for the Clearance Memo and Clearance Report.
 * Users can toggle between the "Clearance Memo" and "Clearance Report".
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Array} props.treeData - Array containing memo sections and related data
 * @param {Object} props.title - Title information related to the memo
 * @param {Object} props.sectionRefs - References for navigating to specific memo sections
 * @param {Object} props.memoDetails - Additional details about the memo/product
 * @returns {JSX.Element} A tabbed card for displaying clearance memo content.
 */
export const ClearanceMemoTabs = ({ treeData, title, sectionRefs, memoDetails }) => {
    const [activeTab, setActiveTab] = useState(0);
    
    const { isLegalUser, isLegalAdmin } = usePermissions();
    
    const tabList = ['Clearance Memo', isLegalAdmin && 'Clearance Report'];

    const tabsContent = [
        <ClearanceMemoCard
            key="clearance-memo-card"
            memoDetails={memoDetails}
            treeData={treeData}
            title={title}
            sectionRefs={sectionRefs}
        />,
        isLegalAdmin && <ClearanceReportCard
                      key="clearance-report-card"
                      treeData={treeData}
                      title={title}
                      memoDetails={memoDetails}
                      sectionRefs={sectionRefs}
        />,
    ];

    // Handles change of active tab
    const goToTab = (tab) => () => {
        setActiveTab(tab);
    };

    // Render tabs dymamically based on tabList
    const renderTabs = () =>
        // Map through the tabList and create Tab components
        tabList.map((tab, idx) => (
            // Create a Tab component for each tab
            <Tab key={`details-tab-${tab}`} onClick={goToTab(idx)} active={idx === activeTab}>
                {tab}
            </Tab>
        ));

    return (
        <Card cardStyle="floating">
            {/* Tabs Navigation */}
            <Tabs role="tablist" style={{ marginBottom: 15 }}>
                {renderTabs()}
            </Tabs>

            {/* Active Tab Content */}
            {tabsContent[activeTab]}
        </Card>
    );
};
